import {useCallback, useEffect, useState, useRef, startTransition} from 'react';
import _debounce, {DebouncedFunc, DebounceSettings} from 'lodash/debounce';

/**
 * A custom hook that returns a debounced version of a callback function.
 * @template T - The type of the callback function.
 * @param {T} callback - The callback function to be debounced.
 * @param {number} [delay=0] - The delay in milliseconds before the debounced function is invoked.
 * @param {DebounceSettings} [options] - The options to configure the debouncing behavior.
 * @returns {DebouncedFunc<T>} - The debounced version of the callback function.
*/
export function useDebouncedCallback<T extends (...args: any[]) => any>(
	callback: T,
	delay: number = 0,
	options?: DebounceSettings,
): DebouncedFunc<T> {
	return useCallback(_debounce(callback, delay, options), [callback, delay, options]);
}

/**
 * A custom hook that returns a debounced version of the input value.
 * @template T - The type of the input value.
 * @param {T} value - The input value to be debounced.
 * @param {number} [delay=0] - The delay in milliseconds before the debounced value is updated.
 * @param {DebounceSettings} [options] - The options to configure the debouncing behavior.
 *@returns {T} - The debounced value.
*/
export function useDebouncedValue<T>(value: T, delay: number = 0, options?: DebounceSettings): T {
	const previousValue = useRef(value);
	const [current, setCurrent] = useState(value);
	const debouncedCallback = useDebouncedCallback(
		(value: T) => startTransition(() => setCurrent(value)),
		delay,
		options,
	);

	useEffect(() => {
		// doesn't trigger the debounce timer initially
		if (value !== previousValue.current) {
			debouncedCallback(value);
			previousValue.current = value;

			// cancel the debounced callback on clean up
			return debouncedCallback.cancel;
		}
	}, [value]);

	return current;
}
